<!-- eslint-disable vuejs-accessibility/label-has-for -->
<template>
  <div
    ref="root"
    :class="classes"
    :style="styles"
  >
    <div
      class="input-container accessibility-outline outline-inset"
      :aria-disabled="disabled"
      :aria-required="isRequired"
      :aria-readonly="isReadOnly"
      :aria-describedby="inputAriaDescribedby"
      :aria-invalid="isAriaInvalid"
      :aria-label="ariaLabel || sanitizedLabel || 'Auswahl'"
      :aria-placeholder="placeholderText"
      :aria-autocomplete="isSearchable ? 'list' : undefined"
      :aria-controls="listboxId"
      :aria-expanded="listActive && !disabled ? 'true' : 'false'"
      @keydown.enter="toggleList"
      @keydown.space="handleSpaceKey"
    >
      <div class="input-container-inner">
        <div
          v-if="iconLeft"
          class="icon-left"
          :aria-label="ariaLabel || toggleLabel"
          :aria-expanded="listActive && !disabled ? 'true' : 'false'"
          aria-haspopup="listbox"
          :aria-controls="listboxId"
          :aria-disabled="disabled"
          @click="[
            !listActive ? toggleList() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
        >
          <BaseIcon :icon="iconLeft" />
        </div>
        <div
          :id="buttonId"
          ref="inputField"
          class="input-field"
          @click="[
            !listActive ? toggleList() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
        >
          <label v-if="label" :for="isSearchable ? baseFieldId : undefined">{{ label }}</label>
          <div
            v-if="isSearchable ? hasItemSelected && multiple : multiple"
            ref="selectedList"
            :class="['input-selected-list']"
          >
            <span
              v-for="(selection, inx) in value"
              ref="selectedListItem"
              :key="`${name}.${selection}`"
              class="input-selected-list--item"
            >
              {{ getItem(selection).label }}{{ isList && inx !== value.length - 1 ? ',&nbsp;' : '' }}
              <span
                v-if="!isList"
                class="input-selected-list--item-icon"
                role="button"
                tabindex="0"
                :tabindex="disabled ? -1 : 0"
                :aria-label="getRemoveLabel(selection)"
                @click.stop="selectItem(getItem(selection))"
                @keydown.enter.stop.prevent="selectItem(getItem(selection))"
                @keydown.space.stop.prevent="selectItem(getItem(selection))"
              >
                <span aria-hidden="true"><i class="fa-light fa-xmark" /></span>
              </span>
            </span>
          </div>
          <div
            v-else-if="isSearchable ? hasItemSelected : true"
            class="input-selected-item accessibility-outline outline-inset"
            role="button"
            tabindex="0"
            :tabindex="disabled ? -1 : 0"
            :aria-expanded="listActive && !disabled ? 'true' : 'false'"
            aria-haspopup="listbox"
            :aria-controls="listboxId"
            :aria-disabled="disabled"
            :aria-describedby="externalErrorId || undefined"
            aria-label="Auswahl"
            @keydown.enter.stop.prevent="[
              toggleList(),
              isSearchable ? $refs.input.focus() : '',
            ]"
            @keydown.space.stop.prevent="[
              toggleList(),
              isSearchable ? $refs.input.focus() : '',
            ]"
          >
            <span
              v-if="hasValue"
              ref="selectedListItem"
            >{{ getItem(value).label }}</span>
          </div>
          <input
            v-if="isSearchable"
            :id="baseFieldId"
            ref="input"
            v-model="search"
            class="accessibility-outline outline-inset"
            :name="name"
            :disabled="disabled"
            :placeholder="placeholderText"
            :autocomplete="autocomplete"
            aria-autocomplete="list"
            :aria-label="ariaLabel || sanitizedLabel"
            :aria-controls="listboxId"
            :aria-expanded="listActive && !disabled ? 'true' : 'false'"
            :aria-describedby="fieldAriaDescribedby"
            :aria-required="isRequired"
            :aria-disabled="disabled"
            :aria-readonly="isReadOnly"
            @input="listActive = true"
            @keydown="onKeyDown($event)"
            @focusin="focused = true"
            @focusout="onFocusOut($event)"
          >
        </div>
        <div
          class="icon-right accessibility-outline outline-inset"
          role="button"
          tabindex="0"
          :tabindex="disabled ? -1 : 0"
          :aria-label="ariaLabel || iconRightLabel"
          :aria-expanded="(!hasValue || !isClearable) && listActive && !disabled ? 'true' : 'false'"
          aria-haspopup="listbox"
          :aria-controls="listboxId"
          :aria-disabled="disabled"
          @click="[
            hasValue && isClearable ? clear($event, { val: multiple ? [] : '' }) : '',
            !hasValue || !isClearable ? toggleList() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
          @keydown.enter.stop.prevent="[
            hasValue && isClearable ? clear($event, { val: multiple ? [] : '' }) : '',
            !hasValue || !isClearable ? toggleList() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
          @keydown.space.stop.prevent="[
            hasValue && isClearable ? clear($event, { val: multiple ? [] : '' }) : '',
            !hasValue || !isClearable ? toggleList() : '',
            isSearchable ? $refs.input.focus() : '',
          ]"
        >
          <BaseIcon
            v-if="hasValue && isClearable && (isSearchable || multiple) || isClearable && hasValue"
            icon="fa-light fa-xmark"
          />
          <BaseIcon
            v-else-if="!hasValue && getIconRight"
            :icon="getIconRight"
          />
          <BaseIcon
            v-else-if="!(isReadOnly || disabled)"
            :icon="defaultIcon"
          />
        </div>
      </div>
      <BaseItemList
        role="listbox"
        :aria-labelledby="buttonId"
        :aria-multiselectable="multiple ? 'true' : 'false'"
        :aria-hidden="listActive ? 'false' : 'true'"
        :root-class="$attrs['root-class']"
        :value="value"
        :list-active="listActive && !disabled"
        :items="items"
        :multiple="multiple"
        :icon-left="iconLeft"
        :visible-items="visibleItems"
        :empty-text="emptyText"
        :search="search"
        :case-sensitive="caseSensitive"
        :list-id="listboxId"
        :labelled-by="buttonId"
        @select="onSelectItem($event)"
        @payload="$emit('payload', $event)"
        @close="toggleList()"
      />
    </div>

    <div
      v-if="hasError || counter"
      class="input-hint"
    >
      <div
        v-if="hasError"
        :id="`${uniqueId}-error`"
        class="input-hint--error"
        role="alert"
        aria-live="assertive"
        v-html="errorMessage"
      />
      <div
        v-if="counter"
        class="input-hint--counter"
      >
        <span>{{ value.length }} {{ max > 0 ? ` | ${max}` : '' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import BaseSelectMixin from '../../../shared/mixins/BaseSelectMixin';

import BaseItemList from './BaseItemList.vue';

export default {
  name: 'BaseSelect',
  components: {
    BaseItemList,
  },
  mixins: [
    BaseSelectMixin,
  ],
  props: {
    value: {
      type: [String, Number, Array, Object],
      required: false,
      default: () => null,
    },
    items: {
      type: Array,
      required: true,
    },
    emptyText: {
      type: String,
      required: false,
      default: undefined,
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      defaultIcon: 'fa-light fa-chevron-down',
      uniqueId: `base-select-${this._uid}`,
    };
  },
  computed: {
    hasValue() {
      if (this.multiple) {
        return this.value === null
          ? false
          : !!this.itemList.filter((x) => this.value.includes(x.value)).length;
      }

      return this.shadowItems.length
        ? !!this.shadowItems.filter((x) => x.value === this.value).length
        : !!this.itemList.filter((x) => x.value === this.value).length;
    },
    itemHeight() {
      return this.$refs.inputField?.scrollHeight || 0;
    },
    buttonId() {
      return `${this.uniqueId}-button`;
    },
    listboxId() {
      return `${this.uniqueId}-listbox`;
    },
    toggleLabel() {
      const base = this.sanitizedLabel || 'Auswahl';
      if (this.disabled) {
        return base;
      }

      return this.listActive ? `${base} schließen` : `${base} öffnen`;
    },
    iconRightLabel() {
      if (this.disabled) {
        return this.sanitizedLabel || 'Auswahl';
      }
      if (this.isClearable && this.hasValue) {
        return 'Auswahl löschen';
      }

      return this.listActive ? 'Auswahlliste schließen' : 'Auswahlliste öffnen';
    },
  },
  methods: {
    onSelectItem(value) {
      // Emit the update event
      this.emitUpdate(value);

      // Close dropdown after selection for single-select only
      if (!this.multiple) {
        // Use setTimeout to ensure the value updates before closing
        setTimeout(() => {
          if (this.listActive) {
            this.listActive = false;
          }
        }, 50);
      }
    },
    handleSpaceKey(event) {
      // allow default behavior when search input is focused
      if (this.isSearchable && this.focused) {
        // Allow space key to work in the search input
        return;
      }

      event.preventDefault();
    },
    getRemoveLabel(selection) {
      const item = this.getItem(selection) || {};
      const label = item.label || selection;

      return `Auswahl ${label} entfernen`;
    },
  },
};
</script>

<style lang="scss" scoped>
.input-container-inner {
  display: grid;
  grid-template-columns: v-bind(gridTemplateColumns);
}
</style>
